import React, { useState, useEffect } from 'react';
import { Intervention } from '../../types';

interface TechnicentreFinalValidationModalProps {
  show: boolean;
  intervention: Intervention;
  selectedEngines: { name: string; index: number }[];
  rejectedEngines: { engine: string; index: number; reason: string }[];
  onSave: () => void;
  onClose: () => void;
}

// ===== Composant TechnicentreFinalValidationModal =====
const TechnicentreFinalValidationModal: React.FC<TechnicentreFinalValidationModalProps> = ({
  show,
  intervention,
  selectedEngines,
  rejectedEngines,
  onSave,
  onClose,
}) => {
  const [dateSR, setDateSR] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  useEffect(() => {
    if (intervention && show) {

      if (intervention.disponibilite_a_partir) {
        const dispoDate = new Date(intervention.disponibilite_a_partir);
        const dispoDateStr = dispoDate.toISOString().slice(0, 10);
        setMinDate(dispoDateStr);

        if (intervention.depart_pour_mission) {
          const departDate = new Date(intervention.depart_pour_mission);
          const departDateStr = departDate.toISOString().slice(0, 10);
          setMaxDate(departDateStr);
        } else {
          setMaxDate(dispoDateStr);
        }

        setDateSR('');
      }
    }
  }, [intervention, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!dateSR) {
      alert('Veuillez saisir une date d\'intervention SR');
      return;
    }

    try {

      const validationStep1Data: any = {
        id: intervention.id
      };

      selectedEngines.forEach((engine) => {
        validationStep1Data[`reponse-${engine.index}`] = 'oui';
      });

      rejectedEngines.forEach((engine) => {
        validationStep1Data[`reponse-${engine.index}`] = 'non';
        validationStep1Data[`raison-${engine.index}`] = engine.reason;
      });

      console.log('=== TechnicentreFinalValidationModal ===');
      console.log('selectedEngines avec index:', selectedEngines);
      console.log('rejectedEngines avec index:', rejectedEngines);
      console.log('validationStep1Data à envoyer:', validationStep1Data);

      const step1Response = await fetch('/api/interventions/validate-technicentre', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validationStep1Data),
      });

      const step1Result = await step1Response.json();

      if (!step1Response.ok) {
        throw new Error(step1Result.error || 'Erreur lors de la validation des engins');
      }

      const validationFinalData: any = {
        id: intervention.id,
        date_intervention_sr: dateSR,
        commentaires: commentaire
      };

      const response = await fetch('/api/interventions/validate-technicentre-final', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validationFinalData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la validation finale');
      }

      alert('✅ ' + result.message);
      onClose();

      window.location.reload();

    } catch (error: any) {
      console.error('Erreur lors de la validation finale:', error);
      alert('❌ Erreur lors de la validation finale: ' + error.message);
    }
  };

  if (!show) return null;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content modal-lg"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '700px' }}
      >
        <div className="relative mb-6">
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
          <h2 className="text-2xl font-bold mb-0" style={{ color: '#10b981' }}>
            Validation de l'intervention
          </h2>
        </div>

        {/* Info box */}
        <div style={{
          backgroundColor: '#dbeafe',
          border: '2px solid #93c5fd',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{
            fontSize: '0.9rem',
            fontWeight: 'bold',
            color: '#1e40af',
            margin: '0 0 0.75rem 0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '0.5rem' }}>ℹ️</span>
            Détails de l'intervention à valider
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0.75rem',
            fontSize: '0.875rem'
          }}>
            <div>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>Prestation:</span>
              <span style={{ marginLeft: '0.5rem', color: '#1e3a8a' }}>
                {intervention.prestation_nom || '-'}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>Contact:</span>
              <span style={{ marginLeft: '0.5rem', color: '#1e3a8a' }}>
                {intervention.contact || '-'}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>Campagne:</span>
              <span style={{ marginLeft: '0.5rem', color: '#1e3a8a' }}>
                {intervention.campagne_nom || '-'}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>N° ELAN:</span>
              <span style={{ marginLeft: '0.5rem', color: '#1e3a8a' }}>
                {intervention.numero_elan || '-'}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>Lieu:</span>
              <span style={{ marginLeft: '0.5rem', color: '#1e3a8a' }}>
                {intervention.site_nom || '-'}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>Dispo:</span>
              <span style={{ marginLeft: '0.5rem', color: '#1e3a8a' }}>
                {formatDate(intervention.disponibilite_a_partir)}
              </span>
            </div>
            <div>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>Départ:</span>
              <span style={{ marginLeft: '0.5rem', color: '#1e3a8a' }}>
                {formatDate(intervention.depart_pour_mission)}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <p style={{
            color: '#374151',
            marginBottom: '1.5rem',
            fontSize: '0.95rem'
          }}>
            Veuillez remplir les informations suivantes pour valider cette intervention :
          </p>

          {/* Date d'intervention SR */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: '#374151'
            }}>
              Date d'intervention SR :
            </label>
            <input
              type="date"
              value={dateSR}
              onChange={(e) => setDateSR(e.target.value)}
              min={minDate}
              max={maxDate}
              placeholder="jj/mm/aaaa"
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '0.5rem',
                fontFamily: 'Arial, sans-serif',
                boxSizing: 'border-box'
              }}
            />
            {minDate && maxDate && (
              <p style={{
                fontSize: '0.8rem',
                color: '#6b7280',
                marginTop: '0.25rem',
                marginBottom: 0
              }}>
                La date doit être entre le {formatDateOnly(minDate)} et le {formatDateOnly(maxDate)}
              </p>
            )}
          </div>

          {/* Engins sélectionnés */}
          {selectedEngines.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{
                color: '#6b7280',
                fontSize: '0.9rem',
                marginBottom: '1rem',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '0.5rem'
              }}>
                Engins sélectionnés pour l'intervention
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1rem'
              }}>
                {selectedEngines.map((engine, index) => (
                  <div key={index} style={{
                    color: '#10b981',
                    fontWeight: 500
                  }}>
                    ✓ {engine.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Commentaires */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: '#374151'
            }}>
              Commentaires Général :
            </label>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Ajoutez des informations complémentaires sur cette intervention..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '0.5rem',
                minHeight: '100px',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'Arial, sans-serif'
              }}
            />
          </div>

          {/* Bouton de soumission */}
          <div style={{ marginTop: '1rem' }}>
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#10b981',
                color: '#ffffff',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Valider l'intervention
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TechnicentreFinalValidationModal;
